/**
 * TopBar - Application top navigation bar
 */

import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useAuth } from '../../auth';
import { useSignOutConfirm } from '../../auth/signOut';
import { SessionCountdown } from '@codespeak11/cognito-passwordless';

export const TopBar: FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { requestSignOut, dialog: signOutDialog } = useSignOutConfirm();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    handleMenuClose();
    navigate(path);
  };

  const handleLogout = async () => {
    handleMenuClose();
    requestSignOut();
  };

  // Display email
  const displayEmail = user?.email || 'Not logged in';

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar>
        <Box
          component="img"
          src="/bannerLogo.png"
          alt="cxFab"
          onClick={() => navigate('/')}
          sx={{
            height: 70,
            width: 'auto',
            mr: 2,
            cursor: 'pointer',
            borderRadius: 1,
            display: 'block',
          }}
        />

        <Box sx={{ flexGrow: 1 }} />
        {/* Nothing renders until the session is within 15 minutes of expiring. */}
        <SessionCountdown />

        <IconButton
          onClick={handleMenuOpen}
          size="small"
          aria-controls="user-menu"
          aria-haspopup="true"
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            <AccountCircleIcon />
          </Avatar>
        </IconButton>

        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 200, mt: 1 },
          }}
        >
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {displayEmail}
            </Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleNavigate('/analytics')}>
            <ListItemIcon>
              <AnalyticsIcon fontSize="small" />
            </ListItemIcon>
            Analytics
          </MenuItem>
          <MenuItem onClick={() => handleNavigate('/optimization')}>
            <ListItemIcon>
              <AutoFixHighIcon fontSize="small" />
            </ListItemIcon>
            Optimizations
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleNavigate('/settings')}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          {isAuthenticated && (
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          )}
        </Menu>
        {signOutDialog}
      </Toolbar>
    </AppBar>
  );
};
